/**
 * Lightbox для видео (YouTube и прямые ссылки)
 */

import { useEffect } from 'react';
import { extractYouTubeId } from './VideoPreview';
import { getVideoType } from '@/utils/mediaParser';
import { useVideoProxy } from '@/hooks/useVideoProxy';
import { useScrollLock } from '@/hooks/useScrollLock';
import { useConfigStore } from '@/store/configStore';
import styles from './VideoGallery.module.css';

interface VideoLightboxProps {
  /** URL видео (YouTube или прямая ссылка) */
  url: string;
  /** Подпись к видео */
  caption?: string;
  /** Callback закрытия */
  onClose: () => void;
  /** Callback предыдущее */
  onPrevious?: () => void;
  /** Callback следующее */
  onNext?: () => void;
  /** Есть предыдущее? */
  hasPrevious?: boolean;
  /** Есть следующее? */
  hasNext?: boolean;
}

/**
 * Popup с видео плеером (YouTube iframe или HTML5 video)
 */
export function VideoLightbox({
  url,
  caption,
  onClose,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
}: VideoLightboxProps) {
  const videoType = getVideoType(url);
  const videoId = extractYouTubeId(url);
  const proxiedUrl = useVideoProxy(url);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      } else if (e.key === 'ArrowLeft' && hasPrevious && onPrevious) {
        onPrevious();
      } else if (e.key === 'ArrowRight' && hasNext && onNext) {
        onNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrevious, onNext, hasPrevious, hasNext]);

  useScrollLock(true);

  const t = useConfigStore((s) => s.translations);
  if (videoType === 'youtube' && videoId) {
    const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;

    return (
      <div className={styles.lightbox}>
        <div className={styles.lightboxBackdrop} onClick={onClose} />
        
        <div className={styles.lightboxContent}>
          {/* Close button */}
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label={t.close || 'Close'}
            type="button"
          >
            ×
          </button>

          <div className={styles.playerWrapper}>
            <iframe
              src={embedUrl}
              title={caption || 'YouTube video'}
              className={styles.player}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>

          {/* Caption */}
          {caption && (
            <div className={styles.lightboxCaption}>{caption}</div>
          )}

          {/* Navigation */}
          {hasPrevious && onPrevious && (
            <button 
              className={styles.navButton + ' ' + styles.navPrev}
              onClick={onPrevious}
            aria-label={t.aria_prev_video || 'Previous video'}
            type="button"
          >
              ‹
            </button>
          )}

          {hasNext && onNext && (
            <button 
              className={styles.navButton + ' ' + styles.navNext}
              onClick={onNext}
              aria-label={t.aria_next_video || 'Next video'}
              type="button"
          >
              ›
            </button>
          )}
        </div>
      </div>
    );
  }

  if (videoType === 'direct') {
    return (
      <div className={styles.lightbox}>
        <div className={styles.lightboxBackdrop} onClick={onClose} />
        
        <div className={styles.lightboxContent}>
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label={t.close || 'Close'}
            type="button"
          >
            ×
          </button>

          <div className={styles.videoPlayerWrapper}>
            <video
              src={proxiedUrl || url}
              controls
              autoPlay
              playsInline
              className={styles.videoPlayer}
              title={caption || 'Video'}
            >
              {t.video_unsupported || 'Your browser does not support video playback.'}
            </video>
          </div>

          {caption && (
            <div className={styles.lightboxCaption}>{caption}</div>
          )}

          {hasPrevious && onPrevious && (
            <button 
              className={styles.navButton + ' ' + styles.navPrev}
              onClick={onPrevious}
              aria-label={t.aria_prev_video || 'Previous video'}
              type="button"
            >
              ‹
            </button>
          )}

          {hasNext && onNext && (
            <button 
              className={styles.navButton + ' ' + styles.navNext}
              onClick={onNext}
              aria-label={t.aria_next_video || 'Next video'}
              type="button"
            >
              ›
            </button>
          )}
        </div>
      </div>
    );
  }

  // Неизвестный тип - не показываем
  return null;
}

